import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AdvancedFilter, FilterRule, FieldConfig } from '@/components/ui/advanced-filter'
import { LinkedInViewSelector } from './LinkedInViewSelector'
import { LinkedInColumnManagerDialog } from './LinkedInColumnManagerDialog'
import { useLinkedInViewSettings } from '@/hooks/useLinkedInViewSettings'
import { TableColumnContextMenu } from '@/components/ui/table-column-context-menu'

import { Search, Settings, Edit, ChevronLeft, ChevronRight, Download, Upload, Linkedin, RefreshCw } from 'lucide-react'
import { LinkedInProfile, useSyncLinkedInProfilesFromEvents } from '@/hooks/useLinkedInProfiles'
import { useToast } from '@/hooks/use-toast'
import Papa from 'papaparse'

interface LinkedInTableProps {
  profiles: LinkedInProfile[]
  isLoading: boolean
  searchQuery: string
  onSearchChange: (query: string) => void
  filters: FilterRule[]
  onFiltersChange: (filters: FilterRule[]) => void
  selectedProfiles: string[]
  onSelectionChange: (selected: string[]) => void
  onProfileUpdate: () => void
  onEditProfile: (profile: LinkedInProfile) => void
  onSyncComplete?: () => void
}

const LINKEDIN_FILTER_FIELDS: FieldConfig[] = [
  { key: 'sender_name', label: 'Sender Name', type: 'text' },
  { key: 'linkedin_url', label: 'LinkedIn URL', type: 'text' },
  { key: 'client_name', label: 'Client Name', type: 'text' },
  { key: 'connections_sent', label: 'Connections Sent', type: 'number' },
  { key: 'connections_accepted', label: 'Connections Accepted', type: 'number' },
  { key: 'direct_messages_sent', label: 'Messages Sent', type: 'number' },
  { key: 'message_replies', label: 'Message Replies', type: 'number' },
  { key: 'inmails_sent', label: 'InMails Sent', type: 'number' },
  { key: 'inmail_replies', label: 'InMail Replies', type: 'number' },
  { key: 'sync_status', label: 'Sync Status', type: 'text' },
  { key: 'platform_status', label: 'Platform Status', type: 'text' },
]

export function LinkedInTable({
  profiles,
  isLoading,
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  selectedProfiles,
  onSelectionChange,
  onProfileUpdate,
  onEditProfile,
  onSyncComplete
}: LinkedInTableProps) {
  const { updateView, currentView, currentViewId } = useLinkedInViewSettings()
  const { toast } = useToast()
  const syncProfiles = useSyncLinkedInProfilesFromEvents()
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [showColumnManager, setShowColumnManager] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchQuery)
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    'sender_name', 'linkedin_url', 'client_name', 
    'connections_sent', 'connections_accepted',
    'direct_messages_sent', 'message_replies',
    'inmails_sent', 'inmail_replies',
    'sync_status'
  ])
  const [pinnedColumns, setPinnedColumns] = useState<string[]>([])
  const [columnLabels, setColumnLabels] = useState<Record<string, string>>({
    'sender_name': 'Sender Name',
    'linkedin_url': 'LinkedIn URL',
    'client_name': 'Client',
    // Event-based metrics
    'connections_sent': 'Connections Sent',
    'connections_accepted': 'Connections Accepted',
    'direct_messages_sent': 'Messages Sent',
    'message_replies': 'Message Replies',
    'inmails_sent': 'InMails Sent',
    'inmail_replies': 'InMail Replies',
    // Legacy daily limits (hidden by default)
    'daily_connections': 'Daily Connections',
    'daily_inmails': 'Daily InMails',
    'daily_direct_messages': 'Daily Messages',
    'sync_status': 'Sync Status',
    'platform_status': 'Platform Status',
    'last_synced_at': 'Last Synced'
  })

  // Load column visibility settings
  useEffect(() => {
    const loadColumnConfig = () => {
      try {
        const savedConfig = localStorage.getItem('linkedin-column-config')
        if (savedConfig) {
          const parsedConfig = JSON.parse(savedConfig)
          const visibleColumnIds = parsedConfig
            .filter((col: any) => col.visible)
            .map((col: any) => col.id)
          setVisibleColumns(visibleColumnIds)
        }
        
        // Load pinned columns
        const pinnedConfig = localStorage.getItem('linkedin-pinned-columns')
        if (pinnedConfig) {
          setPinnedColumns(JSON.parse(pinnedConfig))
        }
        
        // Load custom column labels
        const labelConfig = localStorage.getItem('linkedin-column-labels')
        if (labelConfig) {
          const parsedLabels = JSON.parse(labelConfig)
          setColumnLabels(prev => ({ ...prev, ...parsedLabels }))
        }
      } catch (error) {
        console.error('Failed to load column config:', error)
      }
    }
    
    loadColumnConfig()
  }, [])

  // Calculate pagination
  const totalItems = profiles.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedProfiles = profiles.slice(startIndex, endIndex)

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [profiles.length, searchQuery, filters])

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value))
    setCurrentPage(1)
  }

  const handleExport = () => {
    const dataToExport = profiles.map(profile => ({
      sender_name: profile.sender_name,
      linkedin_url: profile.linkedin_url,
      client_name: profile.client_name,
      connections_sent: profile.connections_sent || 0,
      connections_accepted: profile.connections_accepted || 0,
      direct_messages_sent: profile.direct_messages_sent || 0,
      message_replies: profile.message_replies || 0,
      inmails_sent: profile.inmails_sent || 0,
      inmail_replies: profile.inmail_replies || 0,
      daily_connections: profile.daily_connections,
      daily_inmails: profile.daily_inmails,
      daily_direct_messages: profile.daily_direct_messages,
      sync_status: profile.sync_status,
      platform_status: profile.platform_status,
      last_synced_at: profile.last_synced_at
    }))

    const csv = Papa.unparse(dataToExport)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `linkedin-profiles-export-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    toast({
      title: "Export successful",
      description: `${profiles.length} LinkedIn profiles exported to CSV`
    })
  }

  const getSyncStatusBadge = (status: string) => {
    const statusStyles = {
      'synced': 'bg-green-100 text-green-800 border-green-200',
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'failed': 'bg-red-100 text-red-800 border-red-200'
    }
    return (
      <Badge className={statusStyles[status as keyof typeof statusStyles] || statusStyles.pending}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const getPlatformStatusBadge = (status: string) => {
    const normalizedStatus = status?.toLowerCase()
    if (normalizedStatus === 'active') {
      return <Badge className="bg-green-500 text-white">ACTIVE</Badge>
    }
    return <Badge variant="secondary">INACTIVE</Badge>
  }

  // Column management functions
  const handlePinColumn = (columnId: string) => {
    const newPinned = [...pinnedColumns, columnId]
    setPinnedColumns(newPinned)
    localStorage.setItem('linkedin-pinned-columns', JSON.stringify(newPinned))
  }

  const handleUnpinColumn = (columnId: string) => {
    const newPinned = pinnedColumns.filter(id => id !== columnId)
    setPinnedColumns(newPinned)
    localStorage.setItem('linkedin-pinned-columns', JSON.stringify(newPinned))
  }

  const handleHideColumn = (columnId: string) => {
    const newVisible = visibleColumns.filter(id => id !== columnId)
    setVisibleColumns(newVisible)
    
    // Update current view to persist column changes
    updateView(currentViewId, { visibleColumns: newVisible })
    
    // Update localStorage to maintain consistency
    const savedConfig = localStorage.getItem('linkedin-column-config')
    if (savedConfig) {
      const parsedConfig = JSON.parse(savedConfig)
      const updatedConfig = parsedConfig.map((col: any) => 
        col.id === columnId ? { ...col, visible: false } : col
      )
      localStorage.setItem('linkedin-column-config', JSON.stringify(updatedConfig))
    }
  }

  const handleShowColumn = (columnId: string) => {
    if (!visibleColumns.includes(columnId)) {
      const newVisible = [...visibleColumns, columnId]
      setVisibleColumns(newVisible)
      
      // Update current view to persist column changes
      updateView(currentViewId, { visibleColumns: newVisible })
      
      // Update localStorage to maintain consistency
      const savedConfig = localStorage.getItem('linkedin-column-config')
      if (savedConfig) {
        const parsedConfig = JSON.parse(savedConfig)
        const updatedConfig = parsedConfig.map((col: any) => 
          col.id === columnId ? { ...col, visible: true } : col
        )
        localStorage.setItem('linkedin-column-config', JSON.stringify(updatedConfig))
      }
    }
  }

  const handleRenameColumn = (columnId: string, newLabel: string) => {
    setColumnLabels(prev => ({
      ...prev,
      [columnId]: newLabel
    }))
    
    // Save to localStorage
    const newLabels = { ...columnLabels, [columnId]: newLabel }
    localStorage.setItem('linkedin-column-labels', JSON.stringify(newLabels))
  }

  const handleFilterColumn = (columnId: string) => {
    // Implement column-specific filtering if needed
    toast({
      title: "Filter applied",
      description: `Filtering by ${columnLabels[columnId]}`
    })
  }

  const isAllSelected = selectedProfiles.length === paginatedProfiles.length && paginatedProfiles.length > 0

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(paginatedProfiles.map(p => p.id))
    } else {
      onSelectionChange([])
    }
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <CardTitle className="flex items-center gap-2">
              LinkedIn Profiles
              {selectedProfiles.length > 0 && (
                <span className="ml-2 text-sm font-normal text-blue-600">
                  ({selectedProfiles.length} selected)
                </span>
              )}
            </CardTitle>
            <LinkedInViewSelector 
              onViewChange={(view) => {
                // Apply view settings when changed
                setSearchTerm(view.searchQuery)
                setVisibleColumns(view.visibleColumns)
                if (onSearchChange) onSearchChange(view.searchQuery)
                if (onFiltersChange) onFiltersChange(view.filters)
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page:</span>
            <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, URL, or client..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Advanced Filters
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              size="sm"
              onClick={() => {
                syncProfiles.mutate(undefined, {
                  onSuccess: () => {
                    // Update immediately to show current time
                    if (onSyncComplete) {
                      onSyncComplete()
                    }
                    // Then refetch to get updated data
                    setTimeout(() => {
                      onProfileUpdate()
                    }, 1000)
                  }
                })
              }}
              disabled={syncProfiles.isPending}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncProfiles.isPending ? 'animate-spin' : ''}`} />
              Sync from Events
            </Button>
            
            <Button 
              variant="outline"
              size="sm"
              onClick={handleExport}
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>

            <Button 
              variant="outline"
              size="sm"
              onClick={() => setShowColumnManager(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Adjust Columns
            </Button>
          </div>
        </div>
        
        {showAdvancedFilters && (
          <AdvancedFilter
            fields={LINKEDIN_FILTER_FIELDS}
            filters={filters}
            onFiltersChange={onFiltersChange}
            onApply={() => {}}
            onClear={() => onFiltersChange([])}
          />
        )}
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {profiles.length === 0 ? (
            <div className="text-center py-12">
              <Linkedin className="mx-auto h-12 w-12 text-blue-600 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No LinkedIn profiles found</h3>
              <p className="text-sm text-gray-500">
                {profiles.length === 0 ? 'Add LinkedIn profiles to get started' : 'Try adjusting your filters'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-12">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all profiles"
                    />
                  </TableHead>
                  {visibleColumns.includes('sender_name') && (
                    <TableColumnContextMenu
                      columnId="sender_name"
                      columnLabel={columnLabels.sender_name}
                      isPinned={pinnedColumns.includes('sender_name')}
                      isVisible={visibleColumns.includes('sender_name')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.sender_name}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('linkedin_url') && (
                    <TableColumnContextMenu
                      columnId="linkedin_url"
                      columnLabel={columnLabels.linkedin_url}
                      isPinned={pinnedColumns.includes('linkedin_url')}
                      isVisible={visibleColumns.includes('linkedin_url')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.linkedin_url}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('client_name') && (
                    <TableColumnContextMenu
                      columnId="client_name"
                      columnLabel={columnLabels.client_name}
                      isPinned={pinnedColumns.includes('client_name')}
                      isVisible={visibleColumns.includes('client_name')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.client_name}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('daily_connections') && (
                    <TableColumnContextMenu
                      columnId="daily_connections"
                      columnLabel={columnLabels.daily_connections}
                      isPinned={pinnedColumns.includes('daily_connections')}
                      isVisible={visibleColumns.includes('daily_connections')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.daily_connections}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('daily_inmails') && (
                    <TableColumnContextMenu
                      columnId="daily_inmails"
                      columnLabel={columnLabels.daily_inmails}
                      isPinned={pinnedColumns.includes('daily_inmails')}
                      isVisible={visibleColumns.includes('daily_inmails')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.daily_inmails}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('daily_direct_messages') && (
                    <TableColumnContextMenu
                      columnId="daily_direct_messages"
                      columnLabel={columnLabels.daily_direct_messages}
                      isPinned={pinnedColumns.includes('daily_direct_messages')}
                      isVisible={visibleColumns.includes('daily_direct_messages')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.daily_direct_messages}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('connections_sent') && (
                    <TableColumnContextMenu
                      columnId="connections_sent"
                      columnLabel={columnLabels.connections_sent}
                      isPinned={pinnedColumns.includes('connections_sent')}
                      isVisible={visibleColumns.includes('connections_sent')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.connections_sent}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('connections_accepted') && (
                    <TableColumnContextMenu
                      columnId="connections_accepted"
                      columnLabel={columnLabels.connections_accepted}
                      isPinned={pinnedColumns.includes('connections_accepted')}
                      isVisible={visibleColumns.includes('connections_accepted')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.connections_accepted}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('direct_messages_sent') && (
                    <TableColumnContextMenu
                      columnId="direct_messages_sent"
                      columnLabel={columnLabels.direct_messages_sent}
                      isPinned={pinnedColumns.includes('direct_messages_sent')}
                      isVisible={visibleColumns.includes('direct_messages_sent')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.direct_messages_sent}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('message_replies') && (
                    <TableColumnContextMenu
                      columnId="message_replies"
                      columnLabel={columnLabels.message_replies}
                      isPinned={pinnedColumns.includes('message_replies')}
                      isVisible={visibleColumns.includes('message_replies')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.message_replies}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('inmails_sent') && (
                    <TableColumnContextMenu
                      columnId="inmails_sent"
                      columnLabel={columnLabels.inmails_sent}
                      isPinned={pinnedColumns.includes('inmails_sent')}
                      isVisible={visibleColumns.includes('inmails_sent')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.inmails_sent}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('inmail_replies') && (
                    <TableColumnContextMenu
                      columnId="inmail_replies"
                      columnLabel={columnLabels.inmail_replies}
                      isPinned={pinnedColumns.includes('inmail_replies')}
                      isVisible={visibleColumns.includes('inmail_replies')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.inmail_replies}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  {visibleColumns.includes('sync_status') && (
                    <TableColumnContextMenu
                      columnId="sync_status"
                      columnLabel={columnLabels.sync_status}
                      isPinned={pinnedColumns.includes('sync_status')}
                      isVisible={visibleColumns.includes('sync_status')}
                      onPin={handlePinColumn}
                      onUnpin={handleUnpinColumn}
                      onHide={handleHideColumn}
                      onShow={handleShowColumn}
                      onRename={handleRenameColumn}
                      onFilter={handleFilterColumn}
                    >
                      <TableHead className="font-semibold text-xs cursor-pointer">
                        {columnLabels.sync_status}
                      </TableHead>
                    </TableColumnContextMenu>
                  )}
                  <TableHead className="font-semibold text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedProfiles.map((profile) => (
                  <TableRow 
                    key={profile.id}
                    className="hover:bg-muted/50"
                  >
                    <TableCell className="w-12">
                      <Checkbox
                        checked={selectedProfiles.includes(profile.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            onSelectionChange([...selectedProfiles, profile.id])
                          } else {
                            onSelectionChange(selectedProfiles.filter(id => id !== profile.id))
                          }
                        }}
                        aria-label={`Select ${profile.sender_name}`}
                      />
                    </TableCell>
                    {visibleColumns.includes('sender_name') && (
                      <TableCell className="font-medium">
                        {profile.sender_name}
                      </TableCell>
                    )}
                    {visibleColumns.includes('linkedin_url') && (
                      <TableCell>
                        <div className="max-w-[200px] truncate">
                          {profile.linkedin_url}
                        </div>
                      </TableCell>
                    )}
                    {visibleColumns.includes('client_name') && (
                      <TableCell>
                        {profile.client_name || '-'}
                      </TableCell>
                    )}
                    {visibleColumns.includes('daily_connections') && (
                      <TableCell>
                        {profile.daily_connections}
                      </TableCell>
                    )}
                    {visibleColumns.includes('daily_inmails') && (
                      <TableCell>
                        {profile.daily_inmails}
                      </TableCell>
                    )}
                    {visibleColumns.includes('daily_direct_messages') && (
                      <TableCell>
                        {profile.daily_direct_messages}
                      </TableCell>
                    )}
                    {visibleColumns.includes('connections_sent') && (
                      <TableCell>
                        {profile.connections_sent || 0}
                      </TableCell>
                    )}
                    {visibleColumns.includes('connections_accepted') && (
                      <TableCell>
                        {profile.connections_accepted || 0}
                      </TableCell>
                    )}
                    {visibleColumns.includes('direct_messages_sent') && (
                      <TableCell>
                        {profile.direct_messages_sent || 0}
                      </TableCell>
                    )}
                    {visibleColumns.includes('message_replies') && (
                      <TableCell>
                        {profile.message_replies || 0}
                      </TableCell>
                    )}
                    {visibleColumns.includes('inmails_sent') && (
                      <TableCell>
                        {profile.inmails_sent || 0}
                      </TableCell>
                    )}
                    {visibleColumns.includes('inmail_replies') && (
                      <TableCell>
                        {profile.inmail_replies || 0}
                      </TableCell>
                    )}
                    {visibleColumns.includes('sync_status') && (
                      <TableCell>
                        {getSyncStatusBadge(profile.sync_status)}
                      </TableCell>
                    )}
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => onEditProfile(profile)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedProfiles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 2} className="text-center text-muted-foreground">
                      No profiles found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} profiles
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>

      <LinkedInColumnManagerDialog
        open={showColumnManager}
        onOpenChange={setShowColumnManager}
        onColumnsUpdated={(columns) => {
          const visibleColumnIds = columns
            .filter(col => col.visible)
            .map(col => col.id)
          setVisibleColumns(visibleColumnIds)
          updateView(currentViewId, { visibleColumns: visibleColumnIds })
        }}
      />
    </Card>
  )
}